/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Disposable } from '../../../../../base/common/lifecycle.js';
import { AgentHostArtifactToolsConfigKey, platformRootSchema } from '../../../common/agentHostSchema.js';
import { createChatMementoKey, type IAgentHostChatContribution, type IAgentHostChatContributionContext, type IHydrationContext, type IOutgoingTurn, type ISendContribution } from '../../../common/agentHostChatContributionsService.js';
import type { Turn } from '../../../common/state/sessionState.js';
import { IAgentConfigurationService } from '../../agentConfigurationService.js';
import { ARTIFACT_TOOLS_INSTRUCTION } from '../../shared/artifactServerTools.js';

const artifactToolsFirstTurnSeenMemento = createChatMementoKey<boolean>('firstTurnSeen', () => false);

/** Adds artifact-tool guidance to the first turn when artifact tools are enabled. */
export class ArtifactToolsContribution extends Disposable implements IAgentHostChatContribution {

	static readonly id = 'artifactTools';
	readonly order = 200;

	constructor(
		protected readonly _context: IAgentHostChatContributionContext,
		@IAgentConfigurationService private readonly _agentConfigService: IAgentConfigurationService,
	) {
		super();
	}

	onOutgoingTurn(turn: IOutgoingTurn): ISendContribution | undefined {
		const firstTurnSeen = this._context.memento(artifactToolsFirstTurnSeenMemento, turn.chat);
		if (firstTurnSeen.get()) {
			return undefined;
		}
		firstTurnSeen.set(true, undefined);
		return this._agentConfigService.getRootValue(platformRootSchema, AgentHostArtifactToolsConfigKey)
			? { instructions: [ARTIFACT_TOOLS_INSTRUCTION] }
			: undefined;
	}

	onHydrateTurns(context: IHydrationContext, turns: readonly Turn[]): readonly Turn[] {
		if (turns.length > 0) {
			this._context.memento(artifactToolsFirstTurnSeenMemento, context.chat).set(true, undefined);
		}
		return turns;
	}
}
